import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { refreshAnalytics } from '../features/analytics/analyticsSlice';
import Modal from './ui/Modal';
import axios from '../axiosConfig';
import './RefundModal.css';

const REFUND_REASONS = [
  {
    code: 'MERCHANT_CANNOT_FULFILL',
    title: 'Cannot fulfill order',
    description: 'Use this when the store cannot complete the order.',
    policy: 'Customer receives the full eligible amount. Merchant absorbs the service charge.',
    merchantFault: true,
  },
  {
    code: 'ITEM_UNAVAILABLE',
    title: 'Item unavailable',
    description: 'Use this when one or more items cannot be supplied.',
    policy: 'Customer receives the full eligible amount. Merchant absorbs the service charge.',
    merchantFault: true,
  },
  {
    code: 'MERCHANT_CANCELLED',
    title: 'Store cancelled',
    description: 'Use this when the store is cancelling the order.',
    policy: 'Customer receives the full eligible amount. Merchant absorbs the service charge.',
    merchantFault: true,
  },
  {
    code: 'CUSTOMER_CANCELLED',
    title: 'Customer cancelled',
    description: 'Use this when the customer cancels before fulfillment.',
    policy: 'Items, VAT, and delivery are refundable. Service charge stays non-refundable.',
    merchantFault: false,
  },
  {
    code: 'CUSTOMER_CHANGED_MIND',
    title: 'Customer changed mind',
    description: 'Use this when the customer no longer wants the order.',
    policy: 'Items, VAT, and delivery are refundable. Service charge stays non-refundable.',
    merchantFault: false,
  },
];

const MERCHANT_FAULT_CODES = new Set([
  'MERCHANT_CANNOT_FULFILL',
  'ITEM_UNAVAILABLE',
  'MERCHANT_CANCELLED',
]);

const formatCurrency = (value) => new Intl.NumberFormat('en-NG', {
  style: 'currency',
  currency: 'NGN',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(Number(value || 0));

const toMoney = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const getOrderId = (order) => order?.id || order?._id;

const getCustomerName = (order) => {
  const customer = order?.customer || order?.customerId || {};
  const name = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
  return name || 'Guest';
};

const calculatePreview = (order, reasonCode) => {
  const totalPaid = toMoney(order?.total);
  const serviceCharge = toMoney(order?.serviceCharge);
  const serviceChargeWhoPays = String(order?.serviceChargeWhoPays || 'CUSTOMER').toUpperCase();
  const customerPaidServiceCharge = serviceChargeWhoPays === 'CUSTOMER' ? serviceCharge : 0;
  const includeServiceCharge = MERCHANT_FAULT_CODES.has(reasonCode);
  const serviceChargeRefundAmount = includeServiceCharge ? customerPaidServiceCharge : 0;
  const refundAmount = includeServiceCharge
    ? totalPaid
    : toMoney(Math.max(0, totalPaid - customerPaidServiceCharge));

  return {
    totalPaid,
    refundAmount,
    serviceChargeRefundAmount,
    nonRefundableServiceCharge: toMoney(Math.max(0, customerPaidServiceCharge - serviceChargeRefundAmount)),
  };
};

const RefundModal = ({ isOpen, onClose, order, onRefundSuccess }) => {
  const dispatch = useDispatch();
  const [refundReasonCode, setRefundReasonCode] = useState('MERCHANT_CANNOT_FULFILL');
  const [description, setDescription] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const selectedReason = REFUND_REASONS.find(reason => reason.code === refundReasonCode) || REFUND_REASONS[0];
  const preview = useMemo(() => calculatePreview(order, refundReasonCode), [order, refundReasonCode]);

  const reset = () => {
    setRefundReasonCode('MERCHANT_CANNOT_FULFILL');
    setDescription('');
    setError(null);
  };

  const handleClose = () => {
    if (isProcessing) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!order || !selectedReason) return;

    setIsProcessing(true);
    setError(null);

    try {
      const response = await axios.post('/refund-requests/merchant/initiate', {
        orderId: getOrderId(order),
        reason: selectedReason.title,
        description,
        refundReasonCode,
        refundMethod: 'original_payment',
      });

      try {
        await dispatch(refreshAnalytics({ period: 'month' }));
      } catch (analyticsError) {
        console.error('Error refreshing analytics after refund:', analyticsError);
      }

      onRefundSuccess?.(response.data);
      reset();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create refund request');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen || !order) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Request refund"
      size="md"
      padded={false}
      footer={
        <>
          <button
            type="button"
            className="refund-modal-btn refund-modal-btn--secondary"
            onClick={handleClose}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button
            type="button"
            className="refund-modal-btn refund-modal-btn--primary"
            onClick={handleSubmit}
            disabled={isProcessing}
          >
            {isProcessing ? <Loader2 size={14} className="refund-modal-spin" /> : null}
            Submit to admin
          </button>
        </>
      }
    >
      <div className="refund-modal">
        <section className="refund-modal-order">
          <div>
            <span className="refund-modal-label">Order</span>
            <strong>#{order.orderNumber || getOrderId(order)?.slice(-8)}</strong>
          </div>
          <div>
            <span className="refund-modal-label">Customer</span>
            <strong>{getCustomerName(order)}</strong>
          </div>
          <div>
            <span className="refund-modal-label">Paid</span>
            <strong>{formatCurrency(preview.totalPaid)}</strong>
          </div>
        </section>

        <section className="refund-modal-section">
          <label className="refund-modal-field">
            <span className="refund-modal-label">Refund reason</span>
            <select
              value={refundReasonCode}
              onChange={(e) => setRefundReasonCode(e.target.value)}
            >
              {REFUND_REASONS.map((reason) => (
                <option key={reason.code} value={reason.code}>
                  {reason.title}
                </option>
              ))}
            </select>
            <span className="refund-modal-help">{selectedReason.description}</span>
          </label>
        </section>

        <section className={`refund-modal-policy ${selectedReason.merchantFault ? 'is-merchant-fault' : ''}`}>
          <AlertTriangle size={15} />
          <div>
            <strong>{selectedReason.merchantFault ? 'Merchant-side issue' : 'Customer-side issue'}</strong>
            <p>{selectedReason.policy}</p>
          </div>
        </section>

        <section className="refund-modal-breakdown">
          <div>
            <span>Estimated customer refund</span>
            <strong>{formatCurrency(preview.refundAmount)}</strong>
          </div>
          <div>
            <span>Service charge refunded</span>
            <strong>{formatCurrency(preview.serviceChargeRefundAmount)}</strong>
          </div>
          {preview.nonRefundableServiceCharge > 0 && (
            <div>
              <span>Non-refundable service charge</span>
              <strong>{formatCurrency(preview.nonRefundableServiceCharge)}</strong>
            </div>
          )}
        </section>

        <label className="refund-modal-field">
          <span className="refund-modal-label">Notes for admin (optional)</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add context for Revstar Admin before processing..."
            rows={3}
          />
        </label>

        {error && (
          <div className="refund-modal-error" role="alert">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RefundModal;
